import { useState, useEffect } from "react";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Users,
  Plus,
  FileText,
  Globe,
  Download,
  Edit,
  Shield,
  ExternalLink,
  Check,
  X,
  BarChart3,
  Upload,
} from "lucide-react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/vibe-code/rcm-v2/components/ui/card";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import { Label } from "src/vibe-code/rcm-v2/components/ui/label";
import { Separator } from "src/vibe-code/rcm-v2/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/vibe-code/rcm-v2/components/ui/tabs";
import { Textarea } from "src/vibe-code/rcm-v2/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/vibe-code/rcm-v2/components/ui/alert-dialog";
import { DealerForm } from "./forms/DealerForm";
import { EmailAutomations } from "./EmailAutomations";
import type {
  Dealer,
  Employee,
  ActivityLog,
  DealerDocument,
  DealerDocumentType,
  EmailAutomationRule,
} from "src/vibe-code/rcm-v2/types";
import { useDealerDetails } from "src/vibe-code/rcm-api";
import { useEmailAutomations } from "src/vibe-code/rcm-api";
import { formatDistanceToNow, format } from "date-fns";
import { isValidDate } from "src/utils";
import {
  formatDealerType,
  formatEmployeeRole,
  formatEmployeeStatus,
  getDocumentTypeOptions,
} from "../types";
import { LogoUploadDialog } from "src/components/LogoUploadDialog";

const DEALER_TYPE_VARIANTS: Record<
  string,
  "outline" | "secondary" | "default"
> = {
  INDEPENDENT_DEALERSHIP: "outline",
  MOBILE_HOME_MILITIA: "secondary",
};

interface DealerProfileProps {
  dealer: Dealer;
  employees: Employee[];
  onBack: () => void;
  onEmployeeClick: (employee: Employee) => void;
  onAddEmployee: () => void;
  onUpdate: (updatedDealer: Dealer) => void;
}

export function DealerProfile({
  dealer,
  employees,
  onBack,
  onEmployeeClick,
  onAddEmployee,
  onUpdate,
}: DealerProfileProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [documents, setDocuments] = useState<DealerDocument[]>([]);
  const [emailAutomationRules, setEmailAutomationRules] = useState<
    EmailAutomationRule[]
  >([]);
  const [loadingActivityLogs, setLoadingActivityLogs] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingEmailRules, setLoadingEmailRules] = useState(false);
  const [documentOperations, setDocumentOperations] = useState<
    Record<DealerDocumentType, { isUploading: boolean; isRemoving: boolean }>
  >({
    WIRE_INFO: { isUploading: false, isRemoving: false },
    DEALER_PACK: { isUploading: false, isRemoving: false },
    W9_FORM: { isUploading: false, isRemoving: false },
    DISCLOSURE_COPIES: { isUploading: false, isRemoving: false },
  });
  const [deleteDialogOpen, setDeleteDialogOpen] =
    useState<DealerDocumentType | null>(null);
  const {
    getDealerActivityLogs,
    getDealerDocuments,
    createDealerNote,
    downloadDealerDocument,
    uploadDealerDocument,
    removeDealerDocument,
  } = useDealerDetails();

  const { listRules } = useEmailAutomations();

  const dealerEmployees = employees.filter(
    (employee) => employee.dealer.id === dealer.id,
  );

  const documentTypes = getDocumentTypeOptions();

  const findExistingDocument = (
    docType: DealerDocumentType,
  ): DealerDocument | null => {
    return documents.find((doc) => doc.document_type === docType) || null;
  };

  // Fetch activity logs, documents, and email automation rules when dealer changes
  useEffect(() => {
    const fetchData = async () => {
      if (dealer.id) {
        setLoadingActivityLogs(true);
        setLoadingDocuments(true);
        setLoadingEmailRules(true);

        try {
          const [logs, docs, rules] = await Promise.all([
            getDealerActivityLogs(dealer.id),
            getDealerDocuments(dealer.id),
            listRules(dealer.id),
          ]);

          setActivityLogs(logs);
          setDocuments(docs);
          setEmailAutomationRules(rules);
        } catch (error) {
          console.error("Error fetching dealer data:", error);
        } finally {
          setLoadingActivityLogs(false);
          setLoadingDocuments(false);
          setLoadingEmailRules(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer.id]); // Only depend on dealer.id, not the functions to avoid infinite re-renders

  const generateChangeLog = (
    original: Dealer,
    updated: Partial<Dealer>,
  ): string => {
    const changes: string[] = [];

    // Compare key fields that might be updated
    const fieldsToCheck = [
      { key: "name", label: "Dealer Name" },
      { key: "address", label: "Address" },
      { key: "store_number", label: "Store Number" },
      { key: "phone_number", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "website_url", label: "Website" },
      { key: "dealer_type", label: "Dealer Type" },
      { key: "vep_code", label: "VEP Code" },
      { key: "dealer_feedback", label: "Dealer Feedback" },
      { key: "onboarding_date", label: "Onboarding Date" },
      { key: "last_visit_date", label: "Last Visit Date" },
      { key: "uses_dla", label: "Uses DLA" },
    ];

    fieldsToCheck.forEach(({ key, label }) => {
      const originalValue = original[key as keyof Dealer];
      const updatedValue = updated[key as keyof Partial<Dealer>];

      if (updatedValue !== undefined && originalValue !== updatedValue) {
        if (key === "usesDLA") {
          changes.push(
            `${label}: ${originalValue ? "Yes" : "No"} → ${updatedValue ? "Yes" : "No"}`,
          );
        } else {
          const oldVal = originalValue || "(empty)";
          const newVal = updatedValue || "(empty)";
          changes.push(`${label}: ${oldVal} → ${newVal}`);
        }
      }
    });

    // Check lenders array
    if (
      updated.lenders &&
      JSON.stringify(original.lenders) !== JSON.stringify(updated.lenders)
    ) {
      changes.push(`Lenders: Updated to [${updated.lenders.join(", ")}]`);
    }

    return changes.length > 0
      ? `Updated dealer information: ${changes.join("; ")}`
      : "Updated dealer information";
  };

  const handleSave = (updatedDealer: Partial<Dealer>) => {
    // Create the updated dealer object with all fields
    const updatedDealerData: Dealer = {
      ...dealer,
      ...updatedDealer,
      updated_at: new Date().toISOString(),
    };

    // Call the parent's update function
    onUpdate(updatedDealerData);
    setIsEditing(false);
  };

  const handleAddNote = () => {
    setIsAddingNote(true);
  };

  const handleSaveNote = async () => {
    if (newNote.trim()) {
      const success = await createDealerNote(dealer.id, {
        note: newNote.trim(),
      });
      if (success) {
        // Refresh activity logs to show the new note
        const logs = await getDealerActivityLogs(dealer.id);
        setActivityLogs(logs);
        setNewNote("");
        setIsAddingNote(false);
      }
    }
  };

  const handleCancelNote = () => {
    setNewNote("");
    setIsAddingNote(false);
  };

  const handleEmailAllEmployees = () => {
    const activeEmployees = dealerEmployees.filter(
      (employee) =>
        employee.status === "ACTIVE" || employee.status === "ADDED_BY_DEALER",
    );
    const emailAddresses = activeEmployees
      .map((employee) => employee.email)
      .filter(Boolean);
    if (emailAddresses.length > 0) {
      const mailtoLink = `mailto:${emailAddresses.join(",")}`;
      window.open(mailtoLink, "_self");
    }
  };

  const handleEmailRulesUpdate = async (
    updatedRules: EmailAutomationRule[],
  ) => {
    setEmailAutomationRules(updatedRules);
  };

  const updateDocumentOperation = (
    docType: DealerDocumentType,
    updates: Partial<{ isUploading: boolean; isRemoving: boolean }>,
  ) => {
    setDocumentOperations((prev) => ({
      ...prev,
      [docType]: { ...prev[docType], ...updates },
    }));
  };

  const handleDocumentUpload = async (
    docType: DealerDocumentType,
    file: File,
  ) => {
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    // Validate file name length (API limit)
    if (file.name.length > 100) {
      alert("File name must be less than 100 characters");
      return;
    }

    updateDocumentOperation(docType, { isUploading: true });
    try {
      const success = await uploadDealerDocument(dealer.id, docType, file);
      if (success) {
        // Refresh documents
        const updatedDocs = await getDealerDocuments(dealer.id);
        setDocuments(updatedDocs);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      updateDocumentOperation(docType, { isUploading: false });
    }
  };

  const handleDocumentRemove = async (docType: DealerDocumentType) => {
    updateDocumentOperation(docType, { isRemoving: true });
    try {
      const success = await removeDealerDocument(dealer.id, docType);
      if (success) {
        // Refresh documents
        const updatedDocs = await getDealerDocuments(dealer.id);
        setDocuments(updatedDocs);
      }
    } catch (error) {
      console.error("Remove error:", error);
    } finally {
      updateDocumentOperation(docType, { isRemoving: false });
      setDeleteDialogOpen(null);
    }
  };

  const handleDownloadDocument = async (documentType: string) => {
    await downloadDealerDocument(dealer.id, documentType as any);
  };

  if (isEditing) {
    return (
      <DealerForm
        dealer={dealer}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  const concatenateAddressForDealer = (dealer: Dealer) => {
    let concatenatedAddress = "";
    concatenatedAddress += dealer.street1;
    if (dealer.street2) {
      concatenatedAddress += " " + dealer.street2 + " ";
    }
    if (dealer.city && dealer.state) {
      concatenatedAddress += ", ";
      concatenatedAddress += dealer.city;
      concatenatedAddress += ", ";
      concatenatedAddress += dealer.state + " ";
    }
    concatenatedAddress += dealer.zip_code;

    return concatenatedAddress;
  };

  const formatDate = (date: string | undefined) => {
    if (!isValidDate(date)) {
      return "";
    }
    return new Date(date!).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC", // Converts to CDT
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={onBack}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dealers</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{dealer.name}</h1>
            <div className="flex items-center space-x-3 mt-1">
              <Badge
                variant={DEALER_TYPE_VARIANTS[dealer.dealer_type] ?? "outline"}
              >
                {formatDealerType(dealer.dealer_type)}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Dealer
          </Button>
          <Button
            onClick={onAddEmployee}
            className="gradient-primary text-white flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Employee</span>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="employees">
            Employees ({dealerEmployees.length})
          </TabsTrigger>
          <TabsTrigger value="email-automations">
            Email Automations ({emailAutomationRules.length})
          </TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <Card className="shadow-custom-md">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>Basic Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Dealer Type
                  </label>
                  <p className="mt-1">{formatDealerType(dealer.dealer_type)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Address
                  </label>
                  <p className="mt-1">{concatenateAddressForDealer(dealer)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Phone
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{dealer.phone_number}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{dealer.email}</span>
                  </div>
                </div>

                {dealer.website_url && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Website
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <a
                        href={dealer.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-dark flex items-center space-x-1"
                      >
                        <span>
                          {dealer.website_url
                            .replace("https://", "")
                            .replace("http://", "")}
                        </span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}

                <Separator />

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Dealer ID
                  </label>
                  <p className="mt-1 font-mono text-sm">{dealer.id}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    External ID (DLA Link)
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <a
                      href={`https://app.porchpass.com/${dealer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-dark flex items-center space-x-1 font-mono text-sm"
                    >
                      <span className="text-muted-foreground">
                        app.porchpass.com/
                      </span>
                      <span className="text-primary">{dealer.id}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <Separator />

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Logo
                  </label>
                  <div className="flex flex-col gap-3 mt-1">
                    <div className="flex items-center">
                      <img src={`${dealer.logo_url}`} alt="Dealer Logo" />
                    </div>
                    <div className="flex items-center">
                      <LogoUploadDialog dealerId={String(dealer.id)} />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-muted-foreground">Created</label>
                    <div className="font-medium">
                      <div className="text-sm text-gray-500">
                        {isValidDate(dealer.created_at)
                          ? format(new Date(dealer.created_at), "MMM d, yyyy")
                          : ""}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-muted-foreground">Updated</label>
                    <p className="font-medium">
                      {isValidDate(dealer.updated_at)
                        ? formatDistanceToNow(new Date(dealer.updated_at), {
                            addSuffix: true,
                          })
                        : ""}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Information */}
            {dealer.onboarding_date ||
            dealer.last_visit_date ||
            dealer.vep_code ? (
              <Card className="shadow-custom-md">
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Store Number
                    </label>
                    <p className="mt-1 font-mono">{dealer.store_number}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Onboarding Date
                    </label>
                    <p className="mt-1">{formatDate(dealer.onboarding_date)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Last Visit Date
                    </label>
                    <p className="mt-1">{formatDate(dealer.last_visit_date)}</p>
                  </div>
                  {dealer.vep_code && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        VEP Code
                      </label>
                      <p className="mt-1 font-mono">{dealer.vep_code}</p>
                    </div>
                  )}

                  <Separator className="my-4" />

                  {/* DLA Configuration */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      DLA Configuration
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <div
                        className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                          dealer.uses_dla
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-muted-foreground"
                        }`}
                      >
                        {dealer.uses_dla && <Check className="w-3 h-3" />}
                      </div>
                      <span className="font-medium">Uses DLA</span>
                      <span className="text-sm text-muted-foreground">
                        ({dealer.uses_dla ? "Enabled" : "Disabled"})
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-custom-md">
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Store Number
                      </label>
                      <p className="mt-1 font-mono">{dealer.store_number}</p>
                    </div>

                    <p className="text-muted-foreground text-sm">
                      No additional information available
                    </p>

                    <Separator />

                    {/* DLA Configuration */}
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        DLA Configuration
                      </label>
                      <div className="flex items-center space-x-2 mt-1">
                        <div
                          className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                            dealer.uses_dla
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-muted-foreground"
                          }`}
                        >
                          {dealer.uses_dla && <Check className="w-3 h-3" />}
                        </div>
                        <span className="font-medium">Uses DLA</span>
                        <span className="text-sm text-muted-foreground">
                          ({dealer.uses_dla ? "Enabled" : "Disabled"})
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lenders */}
            <Card className="shadow-custom-md">
              <CardHeader>
                <CardTitle>Lenders</CardTitle>
              </CardHeader>
              <CardContent>
                {dealer.lenders && dealer.lenders.length > 0 ? (
                  <div className="space-y-2">
                    {dealer.lenders.map((lender, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="mr-2 mb-2"
                      >
                        {lender}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No lenders specified
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Dealer Feedback */}
            {dealer.dealer_feedback && (
              <Card className="shadow-custom-md lg:col-span-3">
                <CardHeader>
                  <CardTitle>Dealer Feedback</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">
                    {dealer.dealer_feedback}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <Card className="shadow-custom-md lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5" />
                  <span>Quick Stats</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Total Employees</span>
                    <Badge variant="secondary">{dealerEmployees.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">GM Permissions</span>
                    <Badge variant="secondary">
                      {
                        dealerEmployees.filter(
                          (c) => c.role === "GENERAL_MANAGER",
                        ).length
                      }
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Activity Notes</span>
                    <Badge variant="secondary">{activityLogs.length}</Badge>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-foreground">
                    For total applications submitted and month-by-month
                    breakdowns:{" "}
                    <a
                      href="#"
                      className="text-primary underline hover:text-primary/80"
                    >
                      View Data on Superset
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="shadow-custom-md">
            <CardHeader>
              <CardTitle>Document Management</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDocuments ? (
                <div className="text-center p-8">
                  <p className="text-muted-foreground">Loading documents...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documentTypes.map(({ value: docType, label }) => {
                      const existingDoc = findExistingDocument(docType);
                      const operations = documentOperations[docType];

                      return (
                        <div key={docType} className="border rounded-lg p-4">
                          <Label className="font-medium">{label}</Label>
                          {existingDoc ? (
                            <div className="flex items-center justify-between mt-2 p-2 bg-muted rounded">
                              <div className="flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm truncate">
                                  {existingDoc.file_name}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleDownloadDocument(docType)
                                  }
                                  disabled={
                                    operations.isUploading ||
                                    operations.isRemoving
                                  }
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                <AlertDialog
                                  open={deleteDialogOpen === docType}
                                  onOpenChange={(open: boolean) =>
                                    setDeleteDialogOpen(open ? docType : null)
                                  }
                                >
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={
                                        operations.isUploading ||
                                        operations.isRemoving
                                      }
                                    >
                                      {operations.isRemoving ? (
                                        <span className="w-3 h-3 text-xs">
                                          ...
                                        </span>
                                      ) : (
                                        <X className="w-3 h-3" />
                                      )}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Are you sure you want to remove the{" "}
                                        {label}?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone. This will
                                        permanently delete the document from the
                                        system.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancel
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          handleDocumentRemove(docType)
                                        }
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Yes, Remove
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleDocumentUpload(docType, file);
                                  }
                                }}
                                style={{ display: "none" }}
                                id={`file-${docType}`}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  document
                                    .getElementById(`file-${docType}`)
                                    ?.click()
                                }
                                disabled={
                                  operations.isUploading ||
                                  operations.isRemoving
                                }
                                className="w-full"
                              >
                                {operations.isUploading ? (
                                  "Uploading..."
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload {label}
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees">
          <Card className="shadow-custom-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Associated Employees ({dealerEmployees.length})</span>
                </div>
                <div className="flex items-center space-x-2">
                  {dealerEmployees.length > 0 && (
                    <Button
                      onClick={handleEmailAllEmployees}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email All Employees</span>
                    </Button>
                  )}
                  <Button
                    onClick={onAddEmployee}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Employee</span>
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dealerEmployees.length > 0 ? (
                <div className="space-y-3">
                  {dealerEmployees.map((employee) => (
                    <Card
                      key={employee.id}
                      className="shadow-custom-sm cursor-pointer hover:shadow-custom-md transition-shadow"
                      onClick={() => onEmployeeClick(employee)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-medium">
                                {employee.first_name} {employee.last_name}
                              </h4>
                              {employee.role === "GENERAL_MANAGER" && (
                                <div title="GM Permissions">
                                  <Shield className="w-4 h-4 text-accent" />
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {formatEmployeeRole(employee.role)}
                            </p>
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                                <Mail className="w-3 h-3" />
                                <span className="truncate">
                                  {employee.email}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                <span>{employee.phone_number}</span>
                              </div>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs ml-2">
                            {formatEmployeeStatus(employee.status)}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-muted-foreground mb-2">
                    No employees found
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    This dealer doesn't have any employees yet
                  </p>
                  <Button onClick={onAddEmployee} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Employee
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email-automations">
          <EmailAutomations
            dealer={dealer}
            onUpdate={onUpdate}
            emailAutomationRules={emailAutomationRules}
            onEmailRulesUpdate={handleEmailRulesUpdate}
            isLoading={loadingEmailRules}
          />
        </TabsContent>

        <TabsContent value="activity">
          <Card className="shadow-custom-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>Activity Log</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleAddNote}>
                  Add Note
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isAddingNote && (
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground mb-2 block">
                          Add New Note
                        </label>
                        <Textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Enter your note here..."
                          rows={3}
                          className="resize-none"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          onClick={handleSaveNote}
                          disabled={!newNote.trim()}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Save Note
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelNote}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {loadingActivityLogs ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      Loading activity logs...
                    </p>
                  </div>
                ) : activityLogs.length > 0 ? (
                  <div className="space-y-3">
                    {activityLogs.map((log, index) => (
                      <Card key={index} className="shadow-custom-sm">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  Note
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  by {log.created_by}
                                </span>
                              </div>
                              <p className="text-sm mb-2">{log.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {isValidDate(log.created_at)
                                  ? format(
                                      new Date(log.created_at),
                                      "MMM d, yyyy 'at' h:mm a",
                                    )
                                  : ""}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-medium mb-2">No activity yet</h3>
                    <p className="text-muted-foreground mb-4">
                      No activity logs have been recorded for this dealer.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
